import { memo, type SVGProps } from 'react';
import { PieceType, Color } from '@fog-chess/core';
import { getPiecePaths } from './piecePaths';

export interface PieceProps extends SVGProps<SVGSVGElement> {
  /** The piece type (K, Q, R, B, N, P) */
  type: PieceType;
  /** The piece color */
  color: Color;
  /** Square size in pixels (default 56) */
  size?: number;
  /** Whether this piece is currently being dragged */
  isDragging?: boolean;
}

/**
 * Piece — Memoized SVG chess piece.
 *
 * Renders scalable vector graphics for all 6 piece types in both colors.
 * Wrapped in React.memo with a custom comparator to prevent re-renders
 * unless the piece identity, color, size, or drag state actually changes.
 */
export const Piece = memo(function Piece({
  type,
  color,
  size = 56,
  isDragging = false,
  className,
  style,
  ...rest
}: PieceProps) {
  const paths = getPiecePaths(type, color);

  // Color tokens from CSS variables (theme-aware)
  const fillColor = color === 'white' ? 'hsl(var(--card))' : 'hsl(var(--foreground))';
  const strokeColor = color === 'white' ? 'hsl(var(--foreground))' : 'hsl(var(--card))';
  const strokeWidth = 1.2;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${color} ${type}`}
      className={className}
      style={{
        ...style,
        filter: isDragging ? 'drop-shadow(0 4px 8px hsl(var(--primary) / 0.4))' : 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: 'filter 150ms ease, transform 150ms ease',
      }}
      {...rest}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}, (prev, next) => {
  return (
    prev.type === next.type &&
    prev.color === next.color &&
    prev.size === next.size &&
    prev.isDragging === next.isDragging
  );
});

Piece.displayName = 'Piece';